import React, { useState, useEffect } from 'react';
import UsersPage from './UsersPage';
import { InferenceClient } from '@huggingface/inference';
import './App.css';

function App() {
  const [bookName, setBookName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // raw JSON not rendered; rely on parsed fields only
  const [error, setError] = useState('');
  // user-provided HF token (persisted)
  const [hfToken, setHfToken] = useState(() => {
    try { return localStorage.getItem('hf_token') || ''; } catch { return ''; }
  });

  // Local, non-LLM fields for club features
  const [firstPrice, setFirstPrice] = useState(''); // e.g., 12.99
  const [secondPrice, setSecondPrice] = useState('');
  const [pages, setPages] = useState('');
  const [demographics, setDemographics] = useState({ youth: 30, adults: 50, seniors: 20 });
  const [trivia, setTrivia] = useState('');
  const [isbn, setIsbn] = useState('');
  const [libraries] = useState('');
  const [traders] = useState('');
  const [showIsbn, setShowIsbn] = useState(false);
  const [showClub, setShowClub] = useState(false);

  // Metadata fields to show inside Club Details as well
  const [metaAuthor, setMetaAuthor] = useState('');
  const [metaYear, setMetaYear] = useState('');
  const [metaGenre, setMetaGenre] = useState('');
  const [metaSummary, setMetaSummary] = useState('');
  const [metaThemes, setMetaThemes] = useState('');
  const [metaAwards, setMetaAwards] = useState('');

  // Model-driven price position (0-100; 0=cheapest, 100=most expensive)
  const [firstPriceScore, setFirstPriceScore] = useState(0);
  const [secondPriceScore, setSecondPriceScore] = useState(0);
  const [firstPriceLabel, setFirstPriceLabel] = useState('');
  const [secondPriceLabel, setSecondPriceLabel] = useState('');

  // Basic client-side routing without extra deps
  const [path, setPath] = useState(window.location.pathname);
  const navigate = (p) => {
    if (p === path) return;
    window.history.pushState({}, '', p);
    setPath(p);
  };
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!bookName.trim()) return;
    if (!hfToken.trim()) {
      setError('Please enter your Hugging Face token first.');
      return;
    }
    setIsSearching(true);
    setError('');
    // clear prior parsed UI; raw JSON not shown

    (async () => {
      try {
        const userPrompt = `You are a helpful book assistant. Given a book title, return book metadata.

Book Title: "${bookName}"

Reply ONLY with a strict JSON object (no prose, no markdown fences) matching this schema:
{
  "author": string,
  "publication_year": string,
  "genre": string,
  "summary": string,
  "key_themes": string,
  "awards": string,
  "first_hand_price": string,
  "second_hand_price": string,
  "first_hand_price_position": number,   // 0-100 (0=cheapest, 100=most expensive vs general books)
  "second_hand_price_position": number,  // 0-100 (0=cheapest, 100=most expensive vs general books)
  "pages": string,
  "demographics": { "youth": number, "adults": number, "seniors": number },
  "trivia": string,
  "isbn": string,
  "similar_books": [string, string, string, string, string]
}`;

        const client = new InferenceClient(hfToken);
        const completion = await client.chatCompletion({
          model: 'deepseek-ai/DeepSeek-V3-0324',
          messages: [
            { role: 'user', content: userPrompt }
          ],
          max_tokens: 700,
          temperature: 0.2
        });

        let text = completion?.choices?.[0]?.message?.content || '';

        // Strip possible code fences
        if (/^```/m.test(text)) {
          text = text.replace(/^```json\s*|^```\s*|\s*```$/gmi, '').trim();
        }

        // Parse JSON and populate club + metadata fields
        try {
          const data = JSON.parse(text);

          // Prices
          if (data.first_hand_price != null) setFirstPrice(String(data.first_hand_price));
          if (data.second_hand_price != null) setSecondPrice(String(data.second_hand_price));

          // Pages
          if (data.pages != null) setPages(String(data.pages));

          // Demographics -> normalize to percentages
          if (data.demographics && typeof data.demographics === 'object') {
            const y = Number(data.demographics.youth) || 0;
            const a = Number(data.demographics.adults) || 0;
            const s = Number(data.demographics.seniors) || 0;
            const total = y + a + s || 1;
            setDemographics({
              youth: Math.max(0, Math.min(100, Math.round((y / total) * 100))),
              adults: Math.max(0, Math.min(100, Math.round((a / total) * 100))),
              seniors: Math.max(0, Math.min(100, Math.round((s / total) * 100)))
            });
          }

          // Trivia & ISBN
          if (data.trivia != null) setTrivia(String(data.trivia));
          if (data.isbn != null) setIsbn(String(data.isbn));

          // Core metadata (author/year/genre/summary/themes/awards)
          if (data.author != null) setMetaAuthor(String(data.author));
          if (data.publication_year != null) setMetaYear(String(data.publication_year));
          if (data.genre != null) setMetaGenre(String(data.genre));
          if (data.summary != null) setMetaSummary(String(data.summary));
          if (data.key_themes != null) setMetaThemes(String(data.key_themes));
          if (data.awards != null) setMetaAwards(String(data.awards));

          // Price positions (0-100). If missing or invalid, leave at defaults.
          const clamp01 = (n) => Math.max(0, Math.min(100, n));
          const scoreToLabel = (n) => (n <= 33 ? 'Cheap' : n >= 67 ? 'Costly' : 'Average');

          if (typeof data.first_hand_price_position === 'number') {
            const s = clamp01(Math.round(data.first_hand_price_position));
            setFirstPriceScore(s);
            setFirstPriceLabel(scoreToLabel(s));
          } else if (typeof data.first_hand_price_position === 'string') {
            const v = data.first_hand_price_position.toLowerCase();
            const s = v.includes('cheap') ? 20 : v.includes('expensive') ? 85 : 50;
            setFirstPriceScore(s);
            setFirstPriceLabel(v.charAt(0).toUpperCase() + v.slice(1));
          }
          if (typeof data.second_hand_price_position === 'number') {
            const s = clamp01(Math.round(data.second_hand_price_position));
            setSecondPriceScore(s);
            setSecondPriceLabel(scoreToLabel(s));
          } else if (typeof data.second_hand_price_position === 'string') {
            const v = data.second_hand_price_position.toLowerCase();
            const s = v.includes('cheap') ? 20 : v.includes('expensive') ? 85 : 50;
            setSecondPriceScore(s);
            setSecondPriceLabel(v.charAt(0).toUpperCase() + v.slice(1));
          }

          // Reveal section
          setShowClub(true);
        } catch (e) {
          console.warn('Failed to parse JSON from model; hiding club section');
          setShowClub(false);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch details. Check your HF token and try again.');
      } finally {
        setIsSearching(false);
      }
    })();
  };

  const handleInputChange = (e) => {
    setBookName(e.target.value);
  };

  return (
    <div className="App">
      <div className="top-nav">
        <button className={`nav-btn ${path === '/' ? 'active' : ''}`} onClick={() => navigate('/')}>Home</button>
        <button className={`nav-btn ${path === '/users' ? 'active' : ''}`} onClick={() => navigate('/users')}>User</button>
      </div>
      {path === '/users' ? (
        <UsersPage />
      ) : (
      <div className="container">
        <div className="header">
          <h1 className="title">Book Search</h1>
          <p className="subtitle">Discover your next great read</p>
        </div>
        {/* Hugging Face token input */}
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <span className="detail-label">🔑 Hugging Face Token</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              className="input-inline"
              type="password"
              value={hfToken}
              onChange={(e) => setHfToken(e.target.value)}
              placeholder="hf_..."
            />
            <button
              className="toggle"
              type="button"
              onClick={() => { try { localStorage.setItem('hf_token', hfToken); } catch {} }}
            >Save</button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="search-form">
          <div className="input-group">
            <input
              type="text"
              value={bookName}
              onChange={handleInputChange}
              placeholder="Enter book title..."
              className="book-input"
              disabled={isSearching}
              autoComplete="off"
              autoFocus
            />
            <button 
              type="submit" 
              className={`search-button ${isSearching ? 'searching' : ''}`}
              disabled={isSearching || !bookName.trim()}
            >
              <span className="book-emoji">
                {isSearching ? '📖' : '📚'}
              </span>
              <span className="button-text">
                {isSearching ? 'Searching...' : 'Search'}
              </span>
            </button>
          </div>
        </form>
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Club extras (shown after successful JSON parse) */}
        {showClub && (
        <div className="book-details" style={{ marginTop: 20 }}>
          <h3>Book Details</h3>
          <div className="details-grid">
            {metaAuthor && (
              <div className="detail-card"><span className="detail-label">👤 Author</span><p className="detail-value">{metaAuthor}</p></div>
            )}
            {metaYear && (
              <div className="detail-card"><span className="detail-label">🗓️ Publication Year</span><p className="detail-value">{metaYear}</p></div>
            )}
            {metaGenre && (
              <div className="detail-card"><span className="detail-label">🏷️ Genre</span><p className="detail-value">{metaGenre}</p></div>
            )}
            {metaAwards && (
              <div className="detail-card"><span className="detail-label">🏆 Awards</span><p className="detail-value">{metaAwards}</p></div>
            )}
            {metaSummary && (
              <div className="detail-card detail-wide"><span className="detail-label">📖 Summary</span><p className="detail-value">{metaSummary}</p></div>
            )}
            {metaThemes && (
              <div className="detail-card detail-wide"><span className="detail-label">🧩 Key Themes</span><p className="detail-value">{metaThemes}</p></div>
            )}
            <div className="detail-card">
              <span className="detail-label">💵 First-hand Price</span>
              <div className="bar-row" style={{gap: '12px'}}>
                <p className="detail-value" style={{minWidth: '90px', margin: 0}}>{firstPrice || '-'}</p>
                <div className="bar"><div className="bar-fill" style={{ width: `${firstPriceScore}%` }} /></div>
                <span className="bar-scale">{firstPriceLabel || 'Price position'}</span>
              </div>
            </div>

            <div className="detail-card">
              <span className="detail-label">♻️ Second-hand Price</span>
              <div className="bar-row" style={{gap: '12px'}}>
                <p className="detail-value" style={{minWidth: '90px', margin: 0}}>{secondPrice || '-'}</p>
                <div className="bar"><div className="bar-fill" style={{ width: `${secondPriceScore}%` }} /></div>
                <span className="bar-scale">{secondPriceLabel || 'Price position'}</span>
              </div>
            </div>

            <div className="detail-card detail-wide">
              <span className="detail-label">📄 Pages</span>
              <div className="pages-viz">
                <p className="detail-value" style={{minWidth: '60px', margin: 0}}>{pages || '-'}</p>
                <div className="pages-stack">
                  {Array.from({length: Math.min(10, Math.max(0, Math.ceil(Number(pages)/50))) }).map((_,i)=> (
                    <div key={i} className="page-pill" />
                  ))}
                </div>
                <span className="hint">One pill ≈ 50 pages</span>
              </div>
            </div>

            <div className="detail-card detail-wide">
              <span className="detail-label">👥 Reader Demographics</span>
              <div className="demographics">
                <div className="demo-row"><span>Youth</span><div className="bar"><div className="bar-fill" style={{ width: `${demographics.youth}%` }} /></div><span>{demographics.youth}%</span></div>
                <div className="demo-row"><span>Adults</span><div className="bar"><div className="bar-fill" style={{ width: `${demographics.adults}%` }} /></div><span>{demographics.adults}%</span></div>
                <div className="demo-row"><span>Seniors</span><div className="bar"><div className="bar-fill" style={{ width: `${demographics.seniors}%` }} /></div><span>{demographics.seniors}%</span></div>
              </div>
            </div>

            <div className="detail-card detail-wide">
              <span className="detail-label">🧠 Trivia</span>
              <p className="detail-value" style={{whiteSpace: 'pre-wrap', margin: 0}}>{trivia || '-'}</p>
            </div>

            <div className="detail-card">
              <button type="button" className="toggle" onClick={()=>setShowIsbn(!showIsbn)}>
                {showIsbn ? '▼' : '►'} ISBN Details
              </button>
              {showIsbn && (
                <div className="isbn-block">
                  <p className="detail-value" style={{margin: 0}}>{isbn || '-'}</p>
                </div>
              )}
            </div>

            <div className="detail-card">
              <span className="detail-label">📚 Libraries Near Me (availability)</span>
              <p className="detail-value" style={{whiteSpace: 'pre-wrap', margin: 0}}>{libraries || '-'}</p>
            </div>

            <div className="detail-card">
              <span className="detail-label">🔄 People Willing to Trade</span>
              <p className="detail-value" style={{whiteSpace: 'pre-wrap', margin: 0}}>{traders || '-'}</p>
            </div>

          </div>
        </div>
        )}
      </div>
      )}
    </div>
  );
}

export default App;
